import { type FormEvent, useEffect, useRef, useState } from 'react';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Alert, Box, Button, CircularProgress, Container, IconButton, InputAdornment, Paper, TextField, Tooltip } from '@mui/material';
import { archiveApi } from '../api/client';
import type { ArchiveStats, Folder, SearchParams, SearchResponse } from '../api/types';
import { AdvancedSearch } from '../components/AdvancedSearch';
import { AppHeader } from '../components/AppHeader';
import { PageViewer } from '../components/PageViewer';
import { SearchResults } from '../components/SearchResults';
import { StatsStrip } from '../components/StatsStrip';
import { layoutTokens } from '../theme/archiveTheme';

interface ViewerTarget {
  pdfId: number;
  page: number;
}

// The advanced parameters live in the URL alongside the query, so a result list
// someone shares or reloads is the one they were looking at — filters included.
const paramKeys = ['folder', 'pdf', 'from', 'to', 'mode', 'sort', 'variants'] as const;

function readParams(search: string): SearchParams {
  const url = new URLSearchParams(search);
  const out: SearchParams = {};

  for (const key of paramKeys) {
    const value = url.get(key);
    if (value) (out as Record<string, string>)[key] = value;
  }

  return out;
}

export function SearchPage() {
  const initialParams = new URLSearchParams(window.location.search);
  const initialQuery = initialParams.get('q') ?? '';
  const initialOptions = readParams(window.location.search);
  const [query, setQuery] = useState(initialQuery);
  const [options, setOptions] = useState<SearchParams>(initialOptions);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [stats, setStats] = useState<ArchiveStats | null>(null);
  const [result, setResult] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [viewer, setViewer] = useState<ViewerTarget | null>(null);
  const activeSearch = useRef<AbortController | null>(null);

  useEffect(() => {
    archiveApi.stats().then(setStats).catch(() => undefined);
    archiveApi.folders().then(setFolders).catch(() => undefined);
  }, []);

  const runSearch = async (raw: string, opts: SearchParams) => {
    const next = raw.trim();
    activeSearch.current?.abort();
    setError('');

    if (!next) {
      setResult(null);
      window.history.replaceState(null, '', window.location.pathname);
      return;
    }

    const controller = new AbortController();
    activeSearch.current = controller;
    setLoading(true);

    const params = new URLSearchParams({ q: next });
    for (const [key, value] of Object.entries(opts)) {
      if (value) params.set(key, String(value));
    }
    window.history.replaceState(null, '', `?${params.toString()}`);

    try {
      setResult(await archiveApi.search(next, opts, controller.signal));
    } catch (reason) {
      if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      if (activeSearch.current === controller) setLoading(false);
    }
  };

  useEffect(() => {
    const timer = initialQuery
      ? window.setTimeout(() => void runSearch(initialQuery, initialOptions), 0)
      : undefined;
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
      activeSearch.current?.abort();
    };
    // The URL is read once on boot; searches update it without remounting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void runSearch(query, options);
  };

  // Changing a filter re-runs the current search rather than waiting for a second
  // submit: the results on screen would otherwise contradict the controls above
  // them, which is the most convincing way to look broken.
  const changeOptions = (next: SearchParams) => {
    setOptions(next);
    if (query.trim()) void runSearch(query, next);
  };

  return (
    <>
      <AppHeader />
      <Container component="main" maxWidth={false} sx={{ maxWidth: layoutTokens.contentWidth, pb: 10 }}>
        <Box sx={{ pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2.5 } }}>
          <Paper
            component="section"
            variant="outlined"
            aria-label="Archivsuche"
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRadius: `${layoutTokens.panelRadius}px`,
              backgroundColor: 'background.paper',
              boxShadow: (theme) => theme.shadows[1]
            }}
          >
          <Box
            component="form"
            onSubmit={submit}
            sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 1.25, alignItems: 'stretch' }}
          >
            <TextField
              fullWidth
              label="Archiv durchsuchen"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={'Suchbegriff, "genaue Phrase" oder -ausschluss'}
              autoFocus
              inputProps={{ spellCheck: false, 'aria-label': 'Archiv durchsuchen' }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchRoundedIcon color="primary" /></InputAdornment>,
                endAdornment: query ? (
                  <InputAdornment position="end">
                    <Tooltip title="Suchfeld leeren">
                      <IconButton size="small" aria-label="Suchfeld leeren" onClick={() => setQuery('')}>
                        <CloseRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : undefined
              }}
              sx={{ minWidth: 0 }}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? undefined : <SearchRoundedIcon />}
              sx={{ minWidth: { xs: 48, sm: 132 }, px: { xs: 1.5, sm: 2.5 } }}
            >
              {loading
                ? <CircularProgress size={21} color="inherit" />
                : <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Suchen</Box>}
            </Button>
          </Box>
          <AdvancedSearch value={options} folders={folders} onChange={changeOptions} />
          </Paper>
          <Box sx={{ mt: 2.5 }}><StatsStrip stats={stats} /></Box>
        </Box>

        {stats?.has_synthetic && (
          <Alert severity="warning" sx={{ mb: 2 }}>Diese Datenbank enthält synthetisch erzeugte Demo-Texte. Die Seitenbilder zeigen die echten Scans.</Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {result && <SearchResults data={result} stats={stats} onOpen={(pdfId, page) => setViewer({ pdfId, page })} />}
      </Container>

      <PageViewer
        key={viewer ? `${viewer.pdfId}:${viewer.page}` : 'closed'}
        target={viewer}
        query={result?.query ?? ''}
        terms={result?.highlight_terms ?? []}
        onClose={() => setViewer(null)}
        onChange={setViewer}
      />
    </>
  );
}
